import * as Clipboard from "expo-clipboard";
import { useTheme } from "expo-router/react-navigation";
import { Check, Copy } from "lucide-react-native";
import { ComponentRef, FC, useMemo, useRef, useState } from "react";
import { View } from "react-native";

import { Heatmap, HeatmapCell } from "@/components/charts";
import { Box, Card, Center, Pressable, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { QuestionResults } from "@/types/resources";
/* ── Types ─────────────────────────────────────────────── */

interface SingleSelectMatrixQuestionResultsProps {
  compact?: boolean;
  result: QuestionResults;
}

/* ── Component ──────────────────────────────────────────── */

const SingleSelectMatrixQuestionResults: FC<
  SingleSelectMatrixQuestionResultsProps
> = ({ compact = false, result }) => {
  const { i18n } = useLocalization();
  const theme = useTheme();
  const { groups, question } = result;
  const rowLabels = useMemo<string[]>(
    () => question.lines ?? [],
    [question.lines]
  );
  const columnLabels = useMemo<string[]>(
    () => question.options ?? [],
    [question.options]
  );
  const chartRef = useRef<ComponentRef<typeof View>>(null);
  const [copied, setCopied] = useState(false);

  const totalRespondents = useMemo(
    () =>
      groups.reduce(
        (sum, g) =>
          sum +
          g.answers.filter(
            ({ answer }) =>
              (answer.type === "single_select_matrix" ||
                answer.type === "ranking") &&
              answer.choices
          ).length,
        0
      ),
    [groups]
  );

  const title = i18n.t("dashboardNavigator.chartTitleResponseMatrix", {
    count: totalRespondents,
  });

  const heatmapCells = useMemo((): HeatmapCell[] => {
    if (!rowLabels.length || !columnLabels.length) return [];
    const counts: number[][] = rowLabels.map(() => columnLabels.map(() => 0));
    groups.forEach((group) =>
      group.answers.forEach(({ answer }) => {
        if (
          (answer.type === "single_select_matrix" ||
            answer.type === "ranking") &&
          answer.choices
        ) {
          answer.choices.forEach((choice, rowIdx) => {
            if (
              rowIdx < rowLabels.length &&
              choice >= 0 &&
              choice < columnLabels.length
            )
              counts[rowIdx][choice]++;
          });
        }
      })
    );
    return rowLabels.flatMap((_, rowIdx) =>
      columnLabels.map((_, colIdx) => ({
        column: colIdx,
        count: counts[rowIdx][colIdx],
        row: rowIdx,
      }))
    );
  }, [columnLabels, groups, rowLabels]);

  const copyVisual = async () => {
    const element = chartRef.current as unknown as HTMLElement;
    if (!element) return;
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(element, {
        backgroundColor: "#ffffff",
        scale: 2,
      });
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = (reader.result as string).split(",")[1];
          await Clipboard.setImageAsync(base64);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        };
        reader.readAsDataURL(blob);
      }, "image/png");
    } catch (error) {
      console.error("Failed to copy chart:", error);
    }
  };

  if (!heatmapCells.length) {
    return (
      <Center className="h-[200px]">
        <Text className="text-typography-500">
          {i18n.t("dashboardNavigator.noData")}
        </Text>
      </Center>
    );
  }

  return (
    <Card
      className={`group rounded-2xl ${compact ? "border-0 p-0 bg-transparent shadow-none" : "border border-outline-200 p-6"}`}
    >
      <Box
        className={`absolute z-10 opacity-0 group-hover:opacity-100 transition-opacity ${compact ? "top-0 right-0" : "top-4 right-4"}`}
      >
        <Pressable onPress={copyVisual}>
          <Box className="p-2 rounded-lg bg-background-100 dark:bg-background-800 hover:bg-background-200 dark:hover:bg-background-700 transition-colors">
            {copied ? (
              <Check color="rgb(var(--color-success-500))" size={16} />
            ) : (
              <Copy color={theme.colors.text} size={16} />
            )}
          </Box>
        </Pressable>
      </Box>
      <VStack className="gap-4">
        {title && (
          <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100 pr-8">
            {title}
          </Text>
        )}
        <View
          ref={chartRef}
          style={{
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Heatmap
            cells={heatmapCells}
            columnLabels={columnLabels}
            rowLabels={rowLabels}
            totalRespondents={totalRespondents}
          />
        </View>
      </VStack>
    </Card>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default SingleSelectMatrixQuestionResults;
